// Pure, dependency-free helpers shared by the admin bucketed charts
// (HealthStrip, UsageChart, ToolCallsChart, ChannelActivityChart). Kept free
// of React/DOM so they stay unit-testable under bun:test.

const fmtClock = (d: Date): string =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const fmtDay = (d: Date): string => d.toLocaleDateString([], { month: "short", day: "numeric" });

// "Mon DD HH:MM-HH:MM" for the bucket [start, start + bucketSeconds).
export const bucketRangeLabel = (startIso: string, bucketSeconds: number): string => {
  const start = new Date(startIso);
  const end = new Date(start.getTime() + bucketSeconds * 1000);
  return `${fmtDay(start)} ${fmtClock(start)}–${fmtClock(end)}`;
};

// Axis/point label for a single bucket start. Date-only once buckets are 12h
// or wider (a time-of-day there is noise); date + time below that.
export const bucketTimeLabel = (iso: string, bucketSeconds: number): string => {
  const d = new Date(iso);
  return bucketSeconds >= 43200
    ? fmtDay(d)
    : d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

// A "nice" rounded ceiling >= rawMax for an axis top (1/2/5 x 10^n). Degenerate
// or non-finite input collapses to 1 so the axis never shows a 0 or NaN peak.
export const niceAxisMax = (rawMax: number): number => {
  if (!Number.isFinite(rawMax) || rawMax <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawMax));
  const pow = Math.pow(10, exponent);
  const fraction = rawMax / pow;
  const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
  return niceFraction * pow;
};

// `count` descending tick values from max down to 0 inclusive. count <= 1
// yields just [0]; the last element is always exactly 0.
export const axisTicks = (max: number, count: number): ReadonlyArray<number> => {
  if (count <= 1) return [0];
  const ticks: number[] = [];
  const step = max / (count - 1);
  for (let i = 0; i < count - 1; i++) ticks.push(max - step * i);
  ticks.push(0);
  return ticks;
};

export const hasAnyActivity = (buckets: ReadonlyArray<{ activity: boolean }>): boolean =>
  buckets.some((b) => b.activity);

export const activeBucketCount = (buckets: ReadonlyArray<{ activity: boolean }>): number =>
  buckets.reduce((n, b) => (b.activity ? n + 1 : n), 0);

// "$0.0421 this bucket (total $1.20)" — drops the total when not sampled.
export const spendTooltipLine = (deltaUsd: number, spentUsd: number | null): string =>
  spentUsd != null
    ? `$${deltaUsd.toFixed(4)} this bucket (total $${spentUsd.toFixed(2)})`
    : `$${deltaUsd.toFixed(4)} this bucket`;

// "3 ok, 1 error (4 total)".
export const toolCallTooltipLine = (ok: number, error: number): string =>
  `${ok} ok, ${error} error (${ok + error} total)`;

// "41.2%" — the value for one percent-metric bucket (cpu/mem/disk).
export const percentTooltipLine = (value: number): string => `${value.toFixed(1)}%`;

// "41.2%" alone, or "41.2% (1.6 / 4 GB)" when the metric's capacity total (GiB)
// is known. The absolute is percent x total; the total drops its decimal when
// it's a whole number (4 GB, not 4.0 GB).
export const metricValueLabel = (value: number, totalGib: number | null | undefined): string => {
  const pct = percentTooltipLine(value);
  if (totalGib == null || totalGib <= 0) return pct;
  const used = (value / 100) * totalGib;
  const fmtTotal = Number.isInteger(totalGib) ? String(totalGib) : totalGib.toFixed(1);
  return `${pct} (${used.toFixed(1)} / ${fmtTotal} GB)`;
};
